import React from "react";
import { type Season, type ViewMode } from "../../vowGameLogic";
import { SCORE_METADATA } from "../scoreMetadata";
import { MAP_VIEW_ICONS } from "../iconAssets";
import { BUTTON_MUTED, PANEL_CLASS } from "../uiConstants";

type MapViewControlsProps = {
  viewMode: ViewMode;
  onViewChange: (view: ViewMode) => void;
  selectedSeason: Season;
  onSeasonChange: (season: Season) => void;
  seasonOptions: Season[];
};

const SCORE_VIEW_OPTIONS = ["BHQ", "D", "BIO"] as const;

const MAP_VIEW_OPTIONS: Array<{ label: string; value: ViewMode }> = [
  { label: "Habitats", value: "habitat" },
  { label: "Water Level", value: "water_level" },
  ...SCORE_VIEW_OPTIONS.map((value) => ({
    label: SCORE_METADATA[value]?.label ?? value,
    value,
  })),
];

const MapViewControls: React.FC<MapViewControlsProps> = ({
  viewMode,
  onViewChange,
  selectedSeason,
  onSeasonChange,
  seasonOptions,
}) => (
  <div className={`${PANEL_CLASS} space-y-3 p-3 text-xs`}>
    <h3 className="text-center text-sm font-semibold text-slate-900">
      Map Views
    </h3>
    <div className="flex flex-wrap justify-center gap-2">
      {MAP_VIEW_OPTIONS.map((option) => {
        const icon = MAP_VIEW_ICONS[option.value];
        const isSelected = viewMode === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onViewChange(option.value)}
            className={`view-btn ${BUTTON_MUTED} flex items-center justify-center gap-2 ${
              isSelected ? "bg-black text-[#F3F0E6] hover:bg-black" : ""
            }`}
          >
            {icon && (
              <img
                src={icon.src}
                alt={icon.alt}
                className="h-4 w-4 object-contain"
                style={isSelected ? { filter: "brightness(0) invert(1)" } : undefined}
              />
            )}
            <span className={isSelected ? "text-[#F3F0E6]" : "text-black"}>
              {option.label}
            </span>
          </button>
        );
      })}
    </div>

    {viewMode === "water_level" && (
      <SeasonSelector
        selectedSeason={selectedSeason}
        onSeasonChange={onSeasonChange}
        seasonOptions={seasonOptions}
      />
    )}
  </div>
);

type SeasonSelectorProps = {
  selectedSeason: Season;
  onSeasonChange: (season: Season) => void;
  seasonOptions: Season[];
};

const SeasonSelector: React.FC<SeasonSelectorProps> = ({
  selectedSeason,
  onSeasonChange,
  seasonOptions,
}) => (
  <div id="season-selector-container" className="mt-1 text-center text-slate-900">
    <span className="mr-3 font-semibold text-slate-900">Season:</span>
    <div className="inline-flex overflow-hidden border-2 border-black">
      {seasonOptions.map((season, index) => (
        <button
          key={season}
          type="button"
          onClick={() => onSeasonChange(season)}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            selectedSeason === season
              ? "bg-black text-[#F3F0E6] hover:bg-black"
              : "bg-[#BCB7A8] text-black hover:bg-[#a19881]"
          } ${index < seasonOptions.length - 1 ? "border-r-2 border-black" : ""}`}
        >
          {
            ({
              P: "Spring",
              E: "Summer",
              A: "Autumn",
              H: "Winter",
            } as Record<Season, string>)[season]
          }
        </button>
      ))}
    </div>
  </div>
);

export default MapViewControls;
